import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Home, ArrowLeft } from 'lucide-react';

const Breadcrumb = ({ 
  items = [], 
  showBackButton = true, 
  backButtonText = "Back",
  onBackClick = null 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-generate breadcrumb from current path if no items provided
  const generateBreadcrumbFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbItems = [
      { label: 'Dashboard', path: '/overview', icon: <Home size={16} /> }
    ];

    // Map path segments to readable labels
    const pathLabels = {
      'overview': 'Overview',
      'assets': 'Assets',
      'contracts': 'Contracts',
      'finance': 'Finance',
      'insights': 'Insights',
      'ai': 'AI & Intelligence',
      'collaboration': 'Collaboration',
      'integration': 'Integration',
      'projects': 'Projects',
      'system': 'System',
      'management': 'Management',
      'lifecycle': 'Lifecycle',
      'templates': 'Templates',
      'e-signature': 'E-Signature',
      'renewals': 'Renewals',
      'compliance': 'Compliance',
      'assignment': 'Assignment & Usage',
      'maintenance': 'Maintenance & Ops',
      'financial': 'Financial & Valuation',
      'categorization': 'Categorization',
      'tasks': 'Task Management',
      'team': 'Team Collaboration',
      'create': 'Create',
      'edit': 'Edit',
      'view': 'View',
      'dashboard': 'Project Dashboard',
      'milestones': 'Milestone Tracking',
      'risks': 'Risk Management',
      'time-tracking': 'Time Tracking',
      'reports': 'Project Reports',
      'settings': 'Settings',
      'users': 'User Management',
      'roles': 'Role Management',
      'monitoring': 'System Monitoring',
      'master-data': 'Master Data',
      'clause-library': 'Clause Library',
      'erp': 'ERP Integration',
      'procurement': 'Procurement Integration',
      'crm': 'CRM Integration',
      'sso': 'SSO & Access Control',
      'api': 'API Management',
      'hub': 'Collaboration Hub',
      'versioning': 'Version Control',
      'audit': 'Audit Trails',
      'access': 'Access Controls',
      'dashboards': 'Dashboards',
      'analytics': 'Analytics',
      'assetroi': 'Asset ROI Reports',
      'contractvalue': 'Contract Value Reports'
    };

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      let label = pathLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      
      // Special handling for team and tasks routes - they should show under Projects
      if (segment === 'team' || segment === 'tasks') {
        label = 'Projects';
        currentPath = '/projects';
      }
      
      breadcrumbItems.push({
        label,
        path: currentPath,
        isLast: index === pathSegments.length - 1
      });
    });

    return breadcrumbItems;
  };

  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbFromPath();

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      // Use browser history to go back to previous page
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Fallback to parent path or dashboard
        const pathSegments = location.pathname.split('/').filter(segment => segment);
        if (pathSegments.length > 1) {
          const parentPath = '/' + pathSegments.slice(0, -1).join('/');
          navigate(parentPath);
        } else {
          navigate('/dashboard');
        }
      }
    }
  };

  const handleBreadcrumbClick = (path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <div 
      className="breadcrumb-container"
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}
    >
      {/* Back Button */}
      {showBackButton && (
        <button
          className="breadcrumb-back-btn"
          onClick={handleBackClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: 'white',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#2D7DD2',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'var(--font-family)',
            marginBottom: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#2D7DD2';
            e.target.style.color = '#1B66B1';
            e.target.style.backgroundColor = 'rgba(45, 125, 210, 0.05)';
            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = 'rgba(0,0,0,0.08)';
            e.target.style.color = '#2D7DD2';
            e.target.style.backgroundColor = 'white';
            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
          }}
        >
          <ArrowLeft size={16} />
          {backButtonText}
        </button>
      )}

      {/* Breadcrumb Navigation */}
      <nav 
        className="breadcrumb-nav"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontFamily: 'var(--font-family)',
          flexWrap: 'wrap'
        }}
      >
        {breadcrumbItems.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight 
                size={16} 
                style={{ 
                  color: '#6B7280',
                  flexShrink: 0,
                  margin: '0 4px'
                }} 
              />
            )}
            <div
              className={`breadcrumb-item ${item.isLast ? 'breadcrumb-current' : 'breadcrumb-link'}`}
              onClick={() => !item.isLast && handleBreadcrumbClick(item.path)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: item.isLast ? '8px 12px' : '6px 10px',
                borderRadius: '8px',
                cursor: item.isLast ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: item.isLast ? 'transparent' : 'transparent',
                color: item.isLast ? '#1F2937' : '#6B7280',
                fontWeight: item.isLast ? '600' : '500',
                border: item.isLast ? '1px solid transparent' : '1px solid transparent',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                boxShadow: 'none'
              }}
              onMouseEnter={(e) => {
                if (!item.isLast) {
                  e.target.style.backgroundColor = 'rgba(45, 125, 210, 0.05)';
                  e.target.style.color = '#2D7DD2';
                  e.target.style.borderColor = 'transparent';
                  e.target.style.boxShadow = 'none';
                }
              }}
              onMouseLeave={(e) => {
                if (!item.isLast) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6B7280';
                  e.target.style.borderColor = 'transparent';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {item.icon && (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
            </div>
          </React.Fragment>
        ))}
      </nav>
    </div>
  );
};

export default Breadcrumb;
